import { orderedConversationRewardStatuses } from '@/types/meta';
import { CodeDataType, ConversationStatus, RewardFilter } from '@/types/pulse';
import { ConversationWCodes, InterviewCodeType } from '@/types/pulsedb';
import {
  compileFilters,
  ConditionOp,
  filterParams,
  filterSearchString,
  FilterRegistry,
  matchesFilters,
  parseFilters,
  serializeFilters
} from './tableFilters';

export type Operator = '>' | '<' | '=' | '>=' | '<=';

/** A filter on one signal's value. */
export interface CodeFilter {
  codeType: InterviewCodeType;
  value: string;
  operator?: Operator;
}

export type ConversationFilter =
  | { id: 'status'; value: ConversationStatus }
  | { id: 'starred'; value: boolean }
  | { id: 'reward'; value: RewardFilter }
  | ({ id: 'signal' } & CodeFilter);

export type ConversationFilterId = ConversationFilter['id'];

/**
 * The operators a signal filter can be expressed in. A signal's value is stored
 * as text whatever the signal's datatype, and the database offers no numeric
 * reading of that column to compare against, so a comparison would order "10"
 * below "4". Equality is therefore the only operator a query can answer, and a
 * numeric signal is filtered by choosing a value it holds.
 *
 * Comparisons become answerable once `conversation_codes` exposes a numeric
 * reading of `value`. PostgREST cannot cast in a filter — it accepts
 * `value::numeric=gt.4` and silently compares as text — so the reading has to
 * be a computed field: an immutable SQL function `value_num(conversation_codes)`
 * returning the value as numeric where it reads as one and null where it does
 * not, granted to `authenticated`. Adding one is cheap, a metadata change that
 * locks no rows, unlike a stored generated column, which rewrites the table.
 *
 * Four things change with it. This table regains `>`, `<`, `>=` and `<=`;
 * `signalFilter.toPredicate` sends a numeric signal to `value_num` for every
 * operator, equality included, so that "5" and "5.0" count as the same value;
 * `matches` compares numeric signals through `numericCodeValue` so the two
 * backends still agree; and the signal control takes a number and an operator
 * rather than offering the values a signal holds. Index
 * `conversation_codes (codetype_id, value)` at the same time — a filter joins
 * that table once per signal and it carries only its primary key.
 */
const OPERATOR_OPS: Partial<Record<Operator, ConditionOp>> = {
  '=': 'eq'
};

/**
 * The numeric reading of a stored signal value, or null where it has none.
 * Values are matched as text, so this orders a numeric signal's values for the
 * reader rather than deciding what a filter matches. A `value_num` in the
 * database has to read a value exactly as this does, or a signal would filter
 * one way in a query and another way in memory.
 */
const NUMERIC_VALUE =
  /^\s*[-+]?([0-9]+\.?[0-9]*|\.[0-9]+)([eE][-+]?[0-9]+)?\s*$/;

export function numericCodeValue(
  raw: string | null | undefined
): number | null {
  if (raw == null || !NUMERIC_VALUE.test(raw)) return null;
  const value = Number(raw.trim());
  return Number.isFinite(value) ? value : null;
}

/** Whether a signal's values read as numbers, and so order as numbers. */
export function isNumericCodeType(codeType: InterviewCodeType): boolean {
  return (
    codeType.datatype === CodeDataType.NUMBER ||
    codeType.datatype === CodeDataType.FLOAT
  );
}

/**
 * Signal values are free text and routinely carry the characters the `cf`
 * param separates on — a classification option reads "Other issues (e.g.,
 * politics, arguing)" as readily as it reads "low". Encoding the value keeps
 * those out of the grammar; a value with nothing to encode is left as it was,
 * so older links keep working.
 */
function encodeValue(value: string): string {
  return encodeURIComponent(value);
}

function decodeValue(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    // A lone % is not an escape. Take the value as typed rather than dropping
    // a filter over it.
    return raw;
  }
}

const statusFilter = {
  param: 's',
  parse: (raw: string): Extract<ConversationFilter, { id: 'status' }>[] => {
    const value = Number(raw);
    if (!Object.values(ConversationStatus).includes(value)) return [];
    return [{ id: 'status', value: value as ConversationStatus }];
  },
  serialize: (filters) => String(filters[0].value),
  toPredicate: (filter) => ({
    kind: 'column' as const,
    column: 'status',
    op: 'eq' as const,
    value: filter.value
  }),
  matches: (filter, conversation) => conversation.status === filter.value
} satisfies FilterRegistry<
  ConversationFilter,
  ConversationWCodes,
  InterviewCodeType[]
>['status'];

const starredFilter = {
  param: 'starred',
  parse: (raw: string): Extract<ConversationFilter, { id: 'starred' }>[] => {
    if (raw !== 'true' && raw !== 'false') return [];
    return [{ id: 'starred', value: raw === 'true' }];
  },
  serialize: (filters) => String(filters[0].value),
  toPredicate: (filter) => ({
    kind: 'column' as const,
    column: 'starred',
    op: 'eq' as const,
    value: filter.value
  }),
  matches: (filter, conversation) => conversation.starred === filter.value
} satisfies FilterRegistry<
  ConversationFilter,
  ConversationWCodes,
  InterviewCodeType[]
>['starred'];

const rewardFilter = {
  param: 'r',
  parse: (raw: string): Extract<ConversationFilter, { id: 'reward' }>[] => {
    if (raw === 'any') return [{ id: 'reward', value: 'any' }];
    const status = Number(raw);
    if (!orderedConversationRewardStatuses.includes(status)) return [];
    return [{ id: 'reward', value: status }];
  },
  serialize: (filters) => String(filters[0].value),
  // The claim comes back under the relation's own name because the table reads
  // it from there to draw the reward columns. Only one reward filter can be
  // active, so the one join needs no alias to stay distinct.
  toPredicate: (filter) => ({
    kind: 'embedded' as const,
    table: 'conversation_rewards',
    alias: 'conversation_rewards',
    select: 'id, reward, status, email, phone_no, created_at',
    conditions:
      filter.value === 'any'
        ? []
        : [{ column: 'status', op: 'eq' as const, value: filter.value }]
  }),
  matches: (filter, conversation) => {
    const reward = conversation.conversation_rewards;
    const claim = Array.isArray(reward) ? reward[0] : reward;
    if (!claim) return false;
    return filter.value === 'any' || claim.status === filter.value;
  }
} satisfies FilterRegistry<
  ConversationFilter,
  ConversationWCodes,
  InterviewCodeType[]
>['reward'];

const signalFilter = {
  param: 'cf',
  parse: (
    raw: string,
    codeTypes: InterviewCodeType[]
  ): Extract<ConversationFilter, { id: 'signal' }>[] =>
    raw
      .split(',')
      .map((entry) => {
        const separator = entry.indexOf(':');
        if (separator === -1) return null;
        const codeType = codeTypes.find(
          (type) => type.id === Number(entry.slice(0, separator))
        );
        // A signal the interview no longer has cannot be filtered on. Drop the
        // filter rather than failing the page a stale link asks for.
        if (!codeType) return null;

        const rest = entry.slice(separator + 1);
        const pipe = rest.indexOf('|');
        const operator = (pipe === -1 ? '=' : rest.slice(0, pipe)) as Operator;
        // An operator the query cannot express would have to be answered
        // somewhere the row count cannot see. Drop the filter instead.
        if (!(operator in OPERATOR_OPS)) return null;
        const value = decodeValue(pipe === -1 ? rest : rest.slice(pipe + 1));

        return { id: 'signal' as const, codeType, value, operator };
      })
      .filter((filter) => filter !== null),
  serialize: (filters) =>
    filters
      .map((filter) =>
        !filter.operator || filter.operator === '='
          ? `${filter.codeType.id}:${encodeValue(filter.value)}`
          : `${filter.codeType.id}:${filter.operator}|${encodeValue(filter.value)}`
      )
      .join(','),
  // A signal holds its value as text whatever its datatype, so the filter asks
  // for the value as stored: a signal reading "5.0" is matched by "5.0" and not
  // by "5". The controls offer the values a signal holds, so the two agree. A
  // numeric signal reads its own values once `value_num` exists — see
  // OPERATOR_OPS for what that takes.
  toPredicate: (filter) => ({
    kind: 'embedded' as const,
    table: 'conversation_codes',
    select: 'codetype_id, value',
    conditions: [
      { column: 'codetype_id', op: 'eq' as const, value: filter.codeType.id },
      { column: 'value', op: 'eq' as const, value: filter.value }
    ]
  }),
  matches: (filter, conversation) =>
    // A signal can hold several values, so the conversation matches when any
    // one of them does.
    conversation.conversation_codes?.some(
      (code) =>
        code.codetype_id === filter.codeType.id && code.value === filter.value
    ) ?? false
} satisfies FilterRegistry<
  ConversationFilter,
  ConversationWCodes,
  InterviewCodeType[]
>['signal'];

export const CONVERSATION_FILTERS: FilterRegistry<
  ConversationFilter,
  ConversationWCodes,
  InterviewCodeType[]
> = {
  status: statusFilter,
  starred: starredFilter,
  reward: rewardFilter,
  signal: signalFilter
};

/** Every search param the conversation filters own. */
export const CONVERSATION_FILTER_PARAMS = filterParams(CONVERSATION_FILTERS);

export function parseConversationFilters(
  searchParams: Record<string, string | undefined>,
  codeTypes: InterviewCodeType[]
): ConversationFilter[] {
  return parseFilters(searchParams, CONVERSATION_FILTERS, codeTypes);
}

export function serializeConversationFilters(
  filters: ConversationFilter[]
): Record<string, string | null> {
  return serializeFilters(filters, CONVERSATION_FILTERS);
}

export function conversationFilterSearchString(
  filters: ConversationFilter[]
): string {
  return filterSearchString(filters, CONVERSATION_FILTERS);
}

export function compileConversationFilters(filters: ConversationFilter[]) {
  return compileFilters(filters, CONVERSATION_FILTERS);
}

export function conversationMatchesFilters(
  conversation: ConversationWCodes,
  filters: ConversationFilter[]
): boolean {
  return matchesFilters(conversation, filters, CONVERSATION_FILTERS);
}

/**
 * The value of each filter that can only be active once, for the controls that
 * draw it. Null where the filter is not applied.
 */
export function conversationStatusFilter(
  filters: ConversationFilter[]
): ConversationStatus | null {
  const filter = filters.find((candidate) => candidate.id === 'status');
  return filter?.id === 'status' ? filter.value : null;
}

export function conversationStarredFilter(
  filters: ConversationFilter[]
): boolean | null {
  const filter = filters.find((candidate) => candidate.id === 'starred');
  return filter?.id === 'starred' ? filter.value : null;
}

export function conversationRewardFilter(
  filters: ConversationFilter[]
): RewardFilter | null {
  const filter = filters.find((candidate) => candidate.id === 'reward');
  return filter?.id === 'reward' ? filter.value : null;
}

/** Replaces the filters of one kind, leaving the rest as they are. */
export function withConversationFilter(
  filters: ConversationFilter[],
  id: ConversationFilterId,
  replacement: ConversationFilter[]
): ConversationFilter[] {
  return [...filters.filter((filter) => filter.id !== id), ...replacement];
}

/** The active signal filters, in the shape the signal controls work in. */
export function signalFilters(filters: ConversationFilter[]): CodeFilter[] {
  return filters
    .filter((filter) => filter.id === 'signal')
    .map(({ codeType, value, operator }) => ({ codeType, value, operator }));
}

/** Wraps bare signal filters so they can join the rest. */
export function asSignalFilters(
  codeFilters: CodeFilter[]
): ConversationFilter[] {
  return codeFilters.map((filter) => ({ id: 'signal' as const, ...filter }));
}
